import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { SF, formatTND } from "../shared/storefrontTokens";
import { SectionConfigButtonRow } from "@/components/store/shared/SectionConfigButton";
import {
    pickButtonContentFromProps,
    resolveButtons,
} from "@/lib/sections/sectionButtonConfig";
import { resolveEmptyStateMessage } from "@/lib/sections/emptyStateMessage";
import { useTheme } from "@/storefront/theme/ThemeContext";
import { heroDisplayHeadingClass } from "@/storefront/theme/uiTokens";

function resolveTrustIcon(name?: string): React.ComponentType<{ className?: string }> | null {
    if (!name?.trim()) return null;
    const Icon = (LucideIcons as Record<string, React.ComponentType<{ className?: string }>>)[name.trim()];
    return typeof Icon === "function" ? Icon : null;
}

/** Merge `data.props` (legacy) with top-level section props so the editor applies instantly. */
function mergeCarouselConfig(
    flat: Record<string, unknown> & HeroCarouselSectionProps
): Record<string, unknown> {
    const nested =
        flat.data &&
        typeof flat.data === "object" &&
        flat.data !== null &&
        "props" in flat.data &&
        typeof (flat.data as { props?: unknown }).props === "object" &&
        (flat.data as { props: Record<string, unknown> }).props !== null
            ? (flat.data as { props: Record<string, unknown> }).props
            : {};
    return { ...nested, ...flat };
}

export interface HeroCarouselSectionProps {
    autoplay?: boolean;
    /** Schema alias (hero-carousel uses autoPlay) */
    autoPlay?: boolean;
    interval?: number;
    slides?: unknown[];
    heroSlides?: unknown[];
    showArrows?: boolean;
    showDots?: boolean;
    /** 0–100 — darkening over slide imagery for banner slides */
    overlayOpacity?: number;
    /** Vertical size of the carousel stage */
    height?: "sm" | "md" | "lg" | "full";
    /** Banner slides: horizontal alignment of headline + CTAs */
    textAlign?: "left" | "center" | "right";
    /** When false, autoplay keeps running while the cursor is over the carousel */
    pauseOnHover?: boolean;
    isActive?: boolean;
    viewMode?: "desktop" | "mobile";
    sectionType?: string;
    title?: string;
    subtitle?: string;
    secondaryCtaText?: string;
    secondaryCtaLink?: string;
    productBadgeLabel?: string;
    productPrimaryCtaText?: string;
    productSecondaryCtaText?: string;
    productSecondaryCtaLink?: string;
    productPrimaryCtaIcon?: string;
    productTrustItems?: Array<{ id?: string; icon?: string; text?: string }>;
    emptyCarouselMessage?: string;
    data?: {
        products?: unknown[];
        props?: Record<string, unknown>;
    };
}

export const HeroCarouselSection = React.memo<
    HeroCarouselSectionProps & Record<string, unknown>
>((flatProps) => {
    const {
        autoplay: autoplayLegacy = true,
        autoPlay,
        interval = 5000,
        slides: manualSlides,
        heroSlides: manualHeroSlides,
        showArrows = true,
        showDots = true,
        isActive,
        viewMode,
        sectionType,
        data,
    } = flatProps;

    const autoplayOn = autoplayLegacy !== false && autoPlay !== false;

    const cfg = useMemo(() => mergeCarouselConfig(flatProps as Record<string, unknown> & HeroCarouselSectionProps), [flatProps]);

    const storefrontTheme = useTheme();

    const title = typeof cfg.title === "string" ? cfg.title : "";
    const subtitle = typeof cfg.subtitle === "string" ? cfg.subtitle : "";

    const slidesToUse =
        sectionType === "hero-slider"
            ? (manualHeroSlides ?? manualSlides ?? (cfg.heroSlides as unknown[]) ?? (cfg.slides as unknown[]) ?? [])
            : (manualSlides ?? manualHeroSlides ?? (cfg.slides as unknown[]) ?? (cfg.heroSlides as unknown[]) ?? []);

    const isBannerSlides =
        Array.isArray(slidesToUse) &&
        slidesToUse.length > 0 &&
        typeof slidesToUse[0] === "object" &&
        slidesToUse[0] !== null &&
        "title" in (slidesToUse[0] as object) &&
        !("name" in (slidesToUse[0] as object));

    const getSlideImage = (slide: Record<string, unknown>): string => {
        if (!slide || typeof slide !== "object") return "";
        /** Empty string must not win over `image` — `??` only skips null/undefined. */
        const pickUrl = (v: unknown): string | undefined => {
            if (typeof v !== "string") return undefined;
            const t = v.trim();
            return t.length > 0 ? t : undefined;
        };
        const fromString =
            pickUrl(slide.backgroundImage) ?? pickUrl(slide.image) ?? pickUrl(slide.src);
        if (fromString) return fromString;
        const nested = slide.image ?? slide.src;
        if (nested && typeof nested === "object" && typeof (nested as { url?: string }).url === "string") {
            const u = pickUrl((nested as { url: string }).url);
            if (u) return u;
        }
        return "";
    };

    const [currentSlide, setCurrentSlide] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        if (autoplayOn && !isPaused && slidesToUse.length > 0) {
            intervalRef.current = setInterval(() => {
                setCurrentSlide((prev) => (prev + 1) % slidesToUse.length);
            }, interval);
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [autoplayOn, isPaused, slidesToUse.length, interval]);

    const emptyMsg = resolveEmptyStateMessage(cfg, "emptyCarouselMessage");

    const overlayRaw = typeof cfg.overlayOpacity === "number" ? cfg.overlayOpacity : 55;
    const overlayOpacity = Math.max(0, Math.min(100, Number.isFinite(overlayRaw) ? overlayRaw : 55));
    const t = overlayOpacity / 100;

    const heightMode =
        cfg.height === "sm" || cfg.height === "md" || cfg.height === "lg" || cfg.height === "full" ? cfg.height : "lg";
    const bannerAlign =
        cfg.textAlign === "center" || cfg.textAlign === "right" || cfg.textAlign === "left" ? cfg.textAlign : "left";
    const pauseOnHoverOn = cfg.pauseOnHover !== false;

    if (!Array.isArray(slidesToUse) || slidesToUse.length === 0) {
        return (
            <div className="h-[60vh] flex items-center justify-center bg-muted text-muted-foreground">
                {emptyMsg || null}
            </div>
        );
    }

    const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slidesToUse.length);
    const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slidesToUse.length) % slidesToUse.length);

    const isMobileView = viewMode === "mobile";

    const sectionTrackType = sectionType || "hero-carousel";

    const buildBannerSlideButtons = useCallback(
        (slide: Record<string, unknown>): ReturnType<typeof resolveButtons> => {
            const basePick = pickButtonContentFromProps(cfg);
            const secText = typeof cfg.secondaryCtaText === "string" ? cfg.secondaryCtaText.trim() : "";
            const secLinkRaw = typeof cfg.secondaryCtaLink === "string" ? cfg.secondaryCtaLink.trim() : "";
            const secLink = secLinkRaw || "/shop";

            if (Array.isArray(slide.buttons) && slide.buttons.length > 0) {
                const merged = [...(slide.buttons as unknown[])];
                if (secText) {
                    merged.push({ text: secText, link: secLink, style: "outline" });
                }
                return resolveButtons({ ...basePick, buttons: merged }, { sectionKind: "hero" });
            }

            const primaryText = (
                slide.ctaText ??
                slide.buttonText ??
                basePick.buttonText ??
                cfg.buttonText ??
                cfg.ctaText
            ) as string | undefined;
            const primaryLink = (
                slide.ctaLink ??
                slide.buttonLink ??
                basePick.buttonLink ??
                cfg.buttonLink ??
                cfg.ctaLink
            ) as string | undefined;
            const row: Record<string, unknown>[] = [
                {
                    text: typeof primaryText === "string" ? primaryText : "",
                    link: typeof primaryLink === "string" && primaryLink.trim() ? primaryLink : "/shop",
                    style: "solid",
                },
            ];
            if (secText) {
                row.push({ text: secText, link: secLink, style: "outline" });
            }
            return resolveButtons({ ...basePick, buttons: row }, { sectionKind: "hero" });
        },
        [cfg]
    );

    const buildProductSlideButtons = useCallback(
        (product: { slug?: string; id?: string }): ReturnType<typeof resolveButtons> => {
            const slug = String(product?.slug ?? product?.id ?? "").trim();
            const productPath = slug ? `/product/${slug}` : "/shop";
            const primaryText = typeof cfg.productPrimaryCtaText === "string" ? cfg.productPrimaryCtaText : "";
            const secondaryText = typeof cfg.productSecondaryCtaText === "string" ? cfg.productSecondaryCtaText : "";
            const secondaryLinkRaw =
                typeof cfg.productSecondaryCtaLink === "string" ? cfg.productSecondaryCtaLink.trim() : "";
            const secondaryLink = secondaryLinkRaw || "/shop";
            const iconRaw = typeof cfg.productPrimaryCtaIcon === "string" ? cfg.productPrimaryCtaIcon.trim() : "";
            return resolveButtons(
                {
                    buttons: [
                        {
                            text: primaryText,
                            link: productPath,
                            style: "solid",
                            ...(iconRaw ? { icon: iconRaw } : { icon: "ShoppingCart" }),
                        },
                        {
                            text: secondaryText,
                            link: secondaryLink,
                            style: "outline",
                        },
                    ],
                },
                { sectionKind: "hero" }
            );
        },
        [cfg]
    );

    const trustItems = Array.isArray(cfg.productTrustItems)
        ? (cfg.productTrustItems as Array<{ id?: string; icon?: string; text?: string }>).filter(
              (x) => x && typeof x.text === "string" && x.text.trim()
          )
        : [];

    const badgeLabel = typeof cfg.productBadgeLabel === "string" ? cfg.productBadgeLabel.trim() : "";

    const renderSlide = (slide: Record<string, unknown>, index: number) => {
        if (isBannerSlides) {
            const slideTitle = typeof slide.title === "string" ? slide.title : "";
            const slideSubtitle = typeof slide.subtitle === "string" ? slide.subtitle : "";
            const finalBg = getSlideImage(slide);
            const slideKey = String(slide.id ?? `banner-slide-${index}`);
            const ctaButtons = buildBannerSlideButtons(slide);

            return (
                <div
                    key={slideKey}
                    className={cn(
                        "absolute inset-0 w-full h-full transition-all duration-1000",
                        index === currentSlide ? "opacity-100 translate-x-0" : index > currentSlide ? "opacity-0 translate-x-10" : "opacity-0 -translate-x-10"
                    )}
                    style={{ transitionTimingFunction: 'cubic-bezier(0.25,1,0.5,1)' }}
                >
                    {finalBg && (
                        <img
                            src={finalBg}
                            alt={slideTitle || title || ""}
                            className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-110"
                            style={{ transitionDuration: '10000ms' }}
                        />
                    )}
                    <div
                        className="pointer-events-none absolute inset-0"
                        style={{
                            background: `linear-gradient(to top, rgba(0,0,0,${0.22 + 0.58 * t}) 0%, rgba(0,0,0,${0.12 * t}) 42%, rgba(0,0,0,${0.05 * t}) 62%, transparent 100%), linear-gradient(to right, rgba(0,0,0,${0.35 * t}) 0%, transparent 58%)`,
                        }}
                        aria-hidden
                    />
                    <div
                        className={cn(
                            "relative h-full container mx-auto flex max-w-6xl flex-col justify-end px-6 pb-20 md:px-12 md:pb-32",
                            bannerAlign === "center" && "items-center text-center",
                            bannerAlign === "right" && "items-end text-right",
                            bannerAlign === "left" && "items-start text-left"
                        )}
                    >
                        {(slideTitle || title) && (
                            <h2
                                className={heroDisplayHeadingClass(storefrontTheme.ui, {
                                    mobile: Boolean(isMobileView),
                                    productSlide: false,
                                })}
                                style={{ letterSpacing: SF.type.h1.tracking }}
                            >
                                {slideTitle || title}
                            </h2>
                        )}
                        {(slideSubtitle || subtitle) && (
                            <p
                                className={cn(
                                    "opacity-90 mb-8 font-medium animate-fade-in-up max-w-xl",
                                    isMobileView ? "text-lg" : "text-xl md:text-2xl"
                                )}
                            >
                                {slideSubtitle || subtitle}
                            </p>
                        )}
                        <div className="mb-8 w-full animate-fade-in-up">
                            <SectionConfigButtonRow
                                buttons={ctaButtons}
                                rowAlign="left"
                                stackOnMobile
                                gap="lg"
                                trackContext={{ sectionType: sectionTrackType }}
                            />
                        </div>
                    </div>
                </div>
            );
        }

        const product = slide as { id?: string; slug?: string; name?: string; image?: string; price?: number };
        const pid = String(product?.id ?? product?.slug ?? `product-${index}`);
        const ctaButtons = buildProductSlideButtons(product);

        return (
            <div
                key={pid}
                className={cn(
                    "absolute inset-0 transition-all duration-700 ease-in-out",
                    index === currentSlide ? "opacity-100 translate-x-0" : index > currentSlide ? "opacity-0 translate-x-10" : "opacity-0 -translate-x-10"
                )}
            >
                {typeof product.image === "string" && product.image.trim() ? (
                    <img
                        src={product.image}
                        alt={typeof product.name === "string" ? product.name : ""}
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                ) : (
                    <div className="absolute inset-0 bg-foreground" role="img" aria-label={typeof product.name === "string" ? product.name : ""} />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
                <div className="relative h-full container mx-auto px-4 md:px-8 flex flex-col justify-end pb-16 md:pb-20 items-start max-w-5xl">
                    {badgeLabel ? (
                        <span
                            className="px-4 py-1.5 rounded-full text-xs font-bold mb-4 animate-fade-in"
                            style={{ backgroundColor: SF.colors.primary }}
                        >
                            {badgeLabel}
                        </span>
                    ) : null}
                    <h2
                        className={cn(
                            heroDisplayHeadingClass(storefrontTheme.ui, {
                                mobile: Boolean(isMobileView),
                                productSlide: true,
                            }),
                            "max-w-2xl mb-4"
                        )}
                        style={{ letterSpacing: SF.type.h1.tracking }}
                    >
                        {product.name ?? ""}
                    </h2>
                    <p
                        className={cn(
                            "opacity-90 mb-6 font-bold animate-fade-in-up",
                            isMobileView ? "text-xl" : "text-2xl md:text-3xl"
                        )}
                    >
                        {formatTND(Number(product.price ?? 0))}
                    </p>
                    <div className="mb-6 w-full animate-fade-in-up">
                        <SectionConfigButtonRow
                            buttons={ctaButtons}
                            rowAlign="left"
                            stackOnMobile
                            gap="md"
                            trackContext={{ sectionType: sectionTrackType }}
                        />
                    </div>
                    {trustItems.length > 0 ? (
                        <div className="flex flex-wrap gap-2 animate-fade-in">
                            {trustItems.map((item, i) => {
                                const Icon = resolveTrustIcon(item.icon);
                                const key = item.id ? String(item.id) : `trust-${item.text}-${i}`;
                                return (
                                    <span
                                        key={key}
                                        className="inline-flex items-center gap-1.5 rounded-full bg-card/10 backdrop-blur-sm px-3 py-1.5 text-xs font-semibold border border-border/10"
                                    >
                                        {Icon ? <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
                                        {item.text}
                                    </span>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
            </div>
        );
    };

    const stageMinH =
        heightMode === "sm"
            ? isMobileView
                ? "min-h-[40vh]"
                : "min-h-[44vh] md:min-h-[52vh]"
            : heightMode === "md"
              ? isMobileView
                  ? "min-h-[46vh]"
                  : "min-h-[52vh] md:min-h-[64vh]"
              : heightMode === "full"
                ? "min-h-[100dvh]"
                : isMobileView
                  ? "min-h-[50vh]"
                  : "min-h-[60vh] md:min-h-[80vh]";

    return (
        <section
            className={cn(
                "group relative overflow-hidden bg-foreground text-white transition-all duration-300",
                stageMinH,
                isActive ? "ring-2 ring-blue-500 ring-inset" : ""
            )}
            onMouseEnter={() => {
                if (pauseOnHoverOn) setIsPaused(true);
            }}
            onMouseLeave={() => {
                if (pauseOnHoverOn) setIsPaused(false);
            }}
        >
            {slidesToUse.map((slide: unknown, index: number) =>
                renderSlide(slide && typeof slide === "object" ? (slide as Record<string, unknown>) : {}, index)
            )}

            {showArrows && (
                <>
                    <button
                        type="button"
                        aria-label="Slide précédent"
                        onClick={prevSlide}
                        className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-card/10 hover:bg-card/25 backdrop-blur-md transition-all opacity-0 group-hover:opacity-100 flex items-center justify-center"
                    >
                        <ChevronLeft className="h-6 w-6 text-white" aria-hidden />
                    </button>
                    <button
                        type="button"
                        aria-label="Slide suivant"
                        onClick={nextSlide}
                        className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-card/10 hover:bg-card/25 backdrop-blur-md transition-all opacity-0 group-hover:opacity-100 flex items-center justify-center"
                    >
                        <ChevronRight className="h-6 w-6 text-white" aria-hidden />
                    </button>
                </>
            )}

            {showDots && (
                <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2" role="tablist" aria-label="Slides du carrousel">
                    {slidesToUse.map((_: unknown, i: number) => {
                        const dotKey =
                            isBannerSlides && slidesToUse[i] && typeof slidesToUse[i] === "object" && (slidesToUse[i] as { id?: string }).id
                                ? String((slidesToUse[i] as { id: string }).id)
                                : `dot-${i}`;
                        return (
                            <button
                                key={dotKey}
                                type="button"
                                role="tab"
                                aria-selected={i === currentSlide}
                                aria-label={`Aller au slide ${i + 1}`}
                                onClick={() => setCurrentSlide(i)}
                                className={cn(
                                    "h-2 rounded-full transition-all duration-300",
                                    i === currentSlide ? "w-8 bg-card" : "w-2 bg-card/40 hover:bg-card/70"
                                )}
                            />
                        );
                    })}
                </div>
            )}
        </section>
    );
});
